/**
 * 출력 서식 카탈로그 스토어 (PoC localStorage + pocRowPersist)
 * SSOT: D:\295\erp\CM\design\06_출력서식_카탈로그.md
 *
 * 개인 출력 prefs(loadPrintLayout/gridId)와 저장소를 공유하지 않는다.
 */
import {
  isBdApiEnabled,
  POC_BUCKET,
  loadPocDoc,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';
import {
  REPORT_SCOPE,
  REPORT_DATASETS,
  REPORT_DEFS,
  REPORT_DATASET_BY_ID,
  REPORT_DEF_BY_ID,
  REPORT_SAMPLE_ROWS,
  TPL_DEFAULT_BUDGET_REQ_LINE,
  DATASET_ID,
  REPORT_ID,
} from 'system/reportCatalogSeed';
import {
  defaultReportLayout,
  defaultFieldMap,
  normalizeFieldMap,
  normalizeReportLayout,
} from 'system/reportLayout';

export const REPORT_CATALOG_KEY = 'erp.cm.reportCatalog';
export const REPORT_TENANT_BIND_PREFIX = 'erp.cm.reportTenantBind';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function bindKey(tenantCode) {
  return `${REPORT_TENANT_BIND_PREFIX}|${String(tenantCode || '').trim()}`;
}

function newTemplateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tpl-${crypto.randomUUID()}`;
  }
  return `tpl-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 10)}`;
}

function persistCatalog(value) {
  if (!isBdApiEnabled()) return;
  enqueuePersist(REPORT_CATALOG_KEY, () =>
    upsertPocDoc({
      bucket: POC_BUCKET.REPORT_CATALOG,
      rowUid: 'catalog',
      payload: value,
      globalYn: true,
    }).catch((e) => {
      console.warn('[reportCatalog] persist catalog', e);
    })
  );
}

function persistBind(tenantCode, value) {
  if (!isBdApiEnabled()) return;
  const tc = String(tenantCode || '').trim();
  enqueuePersist(bindKey(tc), () =>
    upsertPocDoc({
      bucket: POC_BUCKET.REPORT_BIND,
      rowUid: tc || 'bind',
      payload: value,
      params: { tenantCode: tc },
    }).catch((e) => {
      console.warn('[reportCatalog] persist bind', e);
    })
  );
}

function buildDefaultBudgetReqTemplate() {
  const report = REPORT_DEF_BY_ID[REPORT_ID.BUDGET_REQ_LINE];
  const dataset = REPORT_DATASET_BY_ID[DATASET_ID.BUDGET_REQ_LINE];
  const fieldMap = defaultFieldMap(dataset);
  const layout = defaultReportLayout({ title: report.label });
  layout.orientation = 'landscape';
  layout.paper = 'A4';
  layout.header = {
    ...layout.header,
    left: { ...layout.header.left, text: '{기관}' },
    center: { ...layout.header.center, text: '{문서명}' },
    right: { ...layout.header.right, text: '{출력일}' },
  };
  return {
    templateId: TPL_DEFAULT_BUDGET_REQ_LINE,
    reportId: report.reportId,
    datasetId: dataset.datasetId,
    displayName: '편성내역_디폴트',
    scope: REPORT_SCOPE.DEFAULT,
    tenantCode: '',
    version: 1,
    layout: normalizeReportLayout(layout, { title: report.label }),
    fieldMap,
    formatHints: { splitBy: '', xlsxSheetPerSplit: false, headerRepeat: true },
    useYn: 'Y',
    copiedFrom: '',
    createdAt: new Date().toISOString(),
  };
}

function ensureSeedTemplates(pack) {
  const list = Array.isArray(pack?.templates) ? [...pack.templates] : [];
  if (!list.some((t) => t.templateId === TPL_DEFAULT_BUDGET_REQ_LINE)) {
    list.push(buildDefaultBudgetReqTemplate());
  }
  return { ...pack, templates: list };
}

export function listDatasets() {
  return REPORT_DATASETS;
}

export function getDataset(datasetId) {
  return REPORT_DATASET_BY_ID[datasetId] || null;
}

export function listReports() {
  return REPORT_DEFS.filter((r) => r.useYn !== 'N');
}

export function getReport(reportId) {
  return REPORT_DEF_BY_ID[reportId] || null;
}

export async function hydrateReportCatalog(tenantCode) {
  try {
    if (isBdApiEnabled()) {
      const doc = await loadPocDoc({
        bucket: POC_BUCKET.REPORT_CATALOG,
        rowUid: 'catalog',
        globalYn: true,
      });
      if (doc?.templates) writeJson(REPORT_CATALOG_KEY, doc);
      const tc = String(tenantCode || '').trim();
      if (tc) {
        const bind = await loadPocDoc({
          bucket: POC_BUCKET.REPORT_BIND,
          rowUid: tc,
          params: { tenantCode: tc },
        });
        if (bind) writeJson(bindKey(tc), bind);
      }
    }
  } catch (e) {
    console.warn('[reportCatalog] hydrate', e);
  }
  return ensureReportCatalog();
}

export function ensureReportCatalog() {
  let data = readJson(REPORT_CATALOG_KEY, null);
  if (!data || !Array.isArray(data.templates)) {
    data = { templates: [], updatedAt: new Date().toISOString() };
  }
  data = ensureSeedTemplates(data);
  writeJson(REPORT_CATALOG_KEY, data);
  return data;
}

function writeCatalog(pack) {
  const next = { ...pack, updatedAt: new Date().toISOString() };
  writeJson(REPORT_CATALOG_KEY, next);
  persistCatalog(next);
  return next;
}

export function getTemplate(templateId) {
  if (!templateId) return null;
  return (ensureReportCatalog().templates || []).find((t) => t.templateId === templateId) || null;
}

/**
 * 디폴트 + 로그인 기관본. 다른 기관 TENANT는 노출하지 않음.
 */
export function listTemplates({ reportId, tenantCode } = {}) {
  const tc = String(tenantCode || '').trim();
  return (ensureReportCatalog().templates || []).filter((t) => {
    if (t.useYn === 'N') return false;
    if (reportId && t.reportId !== reportId) return false;
    if (t.scope === REPORT_SCOPE.DEFAULT) return true;
    return t.scope === REPORT_SCOPE.TENANT && t.tenantCode === tc;
  });
}

/**
 * 불러오기 후보: 문서종류·데이터셋·표시 칸·레이아웃이 갖춰진 서식만.
 * 마스터는 다른 기관본도 복사 원본으로 고를 수 있다.
 */
export function isCompleteTemplate(tpl) {
  if (!tpl || tpl.useYn === 'N') return false;
  const report = getReport(tpl.reportId);
  const dataset = getDataset(tpl.datasetId);
  if (!report || !dataset) return false;
  const dsFields = new Set((dataset.fields || []).map((f) => f.field));
  const mapped = (tpl.fieldMap || []).filter(
    (f) => f && f.visible !== false && dsFields.has(f.field)
  );
  if (!mapped.length) return false;
  const layout = tpl.layout;
  if (!layout || typeof layout !== 'object') return false;
  if (!layout.paper && !String(layout.title || '').trim()) return false;
  return true;
}

export function listCompleteTemplates({
  reportId,
  tenantCode,
  includeOtherTenants = true,
} = {}) {
  const tc = String(tenantCode || '').trim();
  const list = (ensureReportCatalog().templates || []).filter((t) => {
    if (!isCompleteTemplate(t)) return false;
    if (reportId && t.reportId !== reportId) return false;
    if (t.scope === REPORT_SCOPE.DEFAULT) return true;
    if (t.scope !== REPORT_SCOPE.TENANT) return false;
    if (includeOtherTenants) return true;
    return t.tenantCode === tc;
  });
  return list.sort((a, b) => {
    const rank = (t) => {
      if (t.scope === REPORT_SCOPE.DEFAULT) return 0;
      if (t.tenantCode === tc) return 1;
      return 2;
    };
    const d = rank(a) - rank(b);
    if (d) return d;
    return String(a.displayName || '').localeCompare(String(b.displayName || ''), 'ko');
  });
}

function normalizeTemplate(raw, dataset) {
  const report = getReport(raw.reportId) || REPORT_DEF_BY_ID[REPORT_ID.BUDGET_REQ_LINE];
  const ds = dataset || getDataset(raw.datasetId) || getDataset(report.datasetId);
  return {
    ...raw,
    displayName: String(raw.displayName || '').trim() || report.label,
    layout: normalizeReportLayout(raw.layout, { title: raw.layout?.title || report.label }),
    fieldMap: normalizeFieldMap(raw.fieldMap, ds),
    formatHints: {
      splitBy: raw.formatHints?.splitBy || '',
      xlsxSheetPerSplit: raw.formatHints?.xlsxSheetPerSplit === true,
      headerRepeat: raw.formatHints?.headerRepeat !== false,
    },
    useYn: raw.useYn === 'N' ? 'N' : 'Y',
  };
}

export function suggestedDisplayName(reportId, tenantCode) {
  const report = getReport(reportId);
  const label = report?.label || '서식';
  const tc = String(tenantCode || '').trim();
  return tc ? `${label}_${tc}` : label;
}

/**
 * 디폴트는 덮어쓰지 않고 기관본으로만 복사.
 */
export function saveAsTenantCopy({
  fromTemplateId,
  tenantCode,
  displayName,
  layout,
  fieldMap,
  formatHints,
} = {}) {
  const tc = String(tenantCode || '').trim();
  if (!tc) throw new Error('기관코드가 없습니다.');
  const src = getTemplate(fromTemplateId) || listTemplates({ tenantCode: tc })[0];
  if (!src) throw new Error('불러올 서식이 없습니다.');
  const report = getReport(src.reportId);
  const dataset = getDataset(src.datasetId);
  const name = String(displayName || '').trim() || suggestedDisplayName(src.reportId, tc);
  const next = normalizeTemplate(
    {
      templateId: newTemplateId(),
      reportId: src.reportId,
      datasetId: src.datasetId,
      displayName: name,
      scope: REPORT_SCOPE.TENANT,
      tenantCode: tc,
      version: 1,
      layout: layout || src.layout,
      fieldMap: fieldMap || src.fieldMap,
      formatHints: formatHints || src.formatHints,
      useYn: 'Y',
      copiedFrom: src.templateId,
      createdAt: new Date().toISOString(),
    },
    dataset
  );
  if (!report) throw new Error('문서종류가 없습니다.');
  const pack = ensureReportCatalog();
  pack.templates = [...(pack.templates || []), next];
  writeCatalog(pack);
  return next;
}

/** 기관본만 덮어쓰기. 디폴트는 거부. */
export function updateTenantTemplate(templateId, patch, tenantCode) {
  const tc = String(tenantCode || '').trim();
  const cur = getTemplate(templateId);
  if (!cur) throw new Error('서식을 찾을 수 없습니다.');
  if (cur.scope !== REPORT_SCOPE.TENANT || cur.tenantCode !== tc) {
    throw new Error('디폴트 서식은 덮어쓸 수 없습니다. 다른 이름으로 저장하세요.');
  }
  const dataset = getDataset(cur.datasetId);
  const next = normalizeTemplate(
    {
      ...cur,
      ...patch,
      templateId: cur.templateId,
      reportId: cur.reportId,
      datasetId: cur.datasetId,
      scope: REPORT_SCOPE.TENANT,
      tenantCode: tc,
      version: cur.version || 1,
      copiedFrom: cur.copiedFrom,
      createdAt: cur.createdAt,
      updatedAt: new Date().toISOString(),
    },
    dataset
  );
  const pack = ensureReportCatalog();
  pack.templates = (pack.templates || []).map((t) => (t.templateId === templateId ? next : t));
  writeCatalog(pack);
  return next;
}

export function getTenantReportBind(tenantCode) {
  const tc = String(tenantCode || '').trim();
  const key = bindKey(tc);
  let pack = readJson(key, null);
  if (!pack?.byReportId) {
    pack = { tenantCode: tc, byReportId: {}, updatedAt: new Date().toISOString() };
    writeJson(key, pack);
  }
  return pack;
}

export function getActiveTemplateId(tenantCode, reportId) {
  const slot = getTenantReportBind(tenantCode).byReportId?.[reportId];
  return slot?.activeYn === 'Y' ? slot.templateId || '' : '';
}

export function setTenantActiveTemplate(tenantCode, reportId, templateId) {
  const tc = String(tenantCode || '').trim();
  const tpl = getTemplate(templateId);
  if (!tpl) throw new Error('서식을 찾을 수 없습니다.');
  if (tpl.reportId !== reportId) throw new Error('문서종류가 다릅니다.');
  if (tpl.scope === REPORT_SCOPE.TENANT && tpl.tenantCode !== tc) {
    throw new Error('다른 기관 서식은 활성화할 수 없습니다.');
  }
  const pack = getTenantReportBind(tc);
  pack.byReportId = pack.byReportId || {};
  pack.byReportId[reportId] = {
    templateId,
    activeYn: 'Y',
    boundAt: new Date().toISOString(),
  };
  pack.updatedAt = new Date().toISOString();
  writeJson(bindKey(tc), pack);
  persistBind(tc, pack);
  return pack.byReportId[reportId];
}

/**
 * 업무 출력 진입점 스텁. P0에서는 업무 화면 출력 버튼에 연결하지 않음.
 */
export function resolveReport(reportId, ctx = {}) {
  const tc = String(ctx.tenantCode || '').trim();
  const activeId = tc ? getActiveTemplateId(tc, reportId) : '';
  if (activeId) {
    const active = getTemplate(activeId);
    if (active && active.useYn !== 'N') {
      const okDefault = active.scope === REPORT_SCOPE.DEFAULT;
      const okTenant = active.scope === REPORT_SCOPE.TENANT && active.tenantCode === tc;
      if (okDefault || okTenant) return active;
    }
  }
  return (
    (ensureReportCatalog().templates || []).find(
      (t) =>
        t.reportId === reportId &&
        t.scope === REPORT_SCOPE.DEFAULT &&
        t.useYn !== 'N'
    ) || null
  );
}

export function getSampleRows(datasetId) {
  return REPORT_SAMPLE_ROWS[datasetId] || [];
}

export { REPORT_SCOPE, DATASET_ID, REPORT_ID, REPORT_SAMPLE_ROWS };
